<template>
  <div>
    <PageHeader v-if="!isMobile" :title="resource.title" />
    <div v-if="resource" class="max-w-6xl mx-auto my-0 px-4">
      <div class="max-w-2xl mx-auto text-center ">
        <h1 :class="['text-center', isMobile ? 'mt-20' : 'mt-24']">{{ resource.title }}</h1>
        <template v-if="resource.related_people.length">
          <p v-for="(group, groupIndex) in resource.related_people" :key="`group-${groupIndex}`">
            {{ capitalize(group.label) }}
            <span v-for="(person, personIndex) in group.people" :key="`person-${personIndex}`">
              <router-link :to="`/${person.uri.replace('people', 'about')}`">{{ person.title }}</router-link>
              <span v-if="personIndex < group.people.length - 1">, </span>
            </span>
          </p>
        </template>
        <p v-html="resource.description" class="my-4"></p>
        <p v-if="resource.credits" class="my-4">
          Credits: {{ resource.credits }}
        </p>
        <p v-if="resource.type === 'pdf'" class="text-center">
          <a :href="resource.pdf.url" target="_blank">Download ({{ prettysize(resource.pdf.size) }})</a>
        </p>
        <p v-else-if="resource.type === 'website'" class="text-center">
          Website: <a :href="resource.website_url" target="_blank">{{ resource.website_url }}</a>
        </p>        
        <Tags :tags="resource.tags" class="my-4" />
      </div>
      <div class="max-w-4xl mx-auto my-12">
        <div v-if="resource.type === 'image'">
          <div class="w-full relative my-16" :style="calcAspectRatioPadding(resource.image)">
            <img
              v-if="resource.image"
              class="absolute top-0 left-0 w-full h-full"
              :key="resource.image.src"
              :srcset="resource.image.srcset"
            />
          </div>
        </div>
        <div v-else-if="resource.type === 'video'">
          <vue-plyr>
            <div :data-plyr-provider="getVideoUrlProvider(resource.video_url)" :data-plyr-embed-id="getVideoUrlId(resource.video_url)"></div>
          </vue-plyr>
        </div>
      </div>
      <RelatedItems :items="relatedItems" />
    </div>
    <PageFooter />
  </div>
</template>

<script>
import prettysize from 'prettysize'
import { mapState } from 'vuex'
import { capitalize, validateVideoUrl, getVideoUrlProvider, getVideoUrlId, formatDateOrRange, generateMetaTags } from '@/util'
import PageHeader from '@/components/PageHeader'
import Tags from '@/components/Tags'
import RelatedItems from '@/components/RelatedItems'
import PageFooter from '@/components/PageFooter'

export default {
  name: 'Resource',

  metaInfo () {
    return {
      title: this.pageTitle,
      meta: generateMetaTags(this.pageTitle, this.pageDescription, this.pageImage)
    }
  }, 

  components: {
    PageHeader,
    Tags,
    RelatedItems,
    PageFooter
  },

  computed: {
    ...mapState(['resources', 'isMobile']),

    pageTitle () {
      return this.resource ? this.resource.title : null
    },

    pageDescription () {
      return this.resource ? this.resource.description : null
    },

    pageImage () {
      return this.resource && this.resource.image ? this.resource.image.src : null
    },

    slug () {
      return this.$route.params.slug
    },

    relatedItems () {
      const activities = this.resource.related_activities.map(e => {
        e.type = 'Activity'
        e.date = e.begin_date
        e.dateDisplay = formatDateOrRange(e.begin_date, e.end_date)
        return e
      })

      const groups = this.resource.related_activity_groups.map(g => {
        g.type = 'Activity Group'
        g.date = g.begin_date
        g.dateDisplay = formatDateOrRange(g.begin_date, g.end_date)
        return g
      })

      return [...activities, ...groups]
    }
  },

  methods: {
    calcAspectRatioPadding (image) {
      if (!image || !image.dimensions) {
        return ''
      }

      return `padding-top:calc(100%/(${image.dimensions.width}/${image.dimensions.height}));`
    },

    filename (url) {
      const parts = url.split('/')
      return parts[parts.length - 1]
    },

    prettysize,
    capitalize,
    validateVideoUrl,
    getVideoUrlProvider,
    getVideoUrlId
  },

  data () {
    return {
      resource: false
    }
  },

  async mounted () {
    await this.$store.dispatch('getResource', this.slug)
    this.resource = this.resources[this.slug]
    this.$Lazyload.lazyLoadHandler()
  }
}
</script>
